import React, { useState, useMemo, useEffect, useCallback, createContext, useContext } from "react";
import { C, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  DataTable, EmptyState, useToast,
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  Landmark, Building2, Home, Users, User,
  FileText, Receipt, DollarSign, TrendingUp, AlertTriangle,
  Bell, Mail, Send, CheckCircle, XCircle,
  Clock, Search, Plus, Eye, Layers, Percent, Wallet,
  PieChart as PieChartIcon, BarChart3, FileCheck, Archive,
  ScrollText, Activity, MessageSquare, Briefcase,
} from "lucide-react";
import {
  getWaqfProperties, getWaqfUnits, getWaqfTenants, getWaqfContracts,
  getWaqfPayments, getWaqfCorrespondence, getWaqfDocuments, getWaqfEndowments,
  createWaqfProperty, createWaqfUnit, createWaqfTenant, createWaqfContract,
  createWaqfPayment, createWaqfCorrespondence, createWaqfDocument,
} from "./supabaseClient";

/* ═════════════════════════════════════════════
   ✦ Reference date — لحساب ألوان التنبيهات والحالات
   ═════════════════════════════════════════════ */
const TODAY_REF = new Date("2026-06-19");

/* ═════════════════════════════════════════════
   ✦ Helpers
   ═════════════════════════════════════════════ */
function daysBetween(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - TODAY_REF.getTime()) / (24 * 3600 * 1000));
}

function daysOverdue(dueIso) {
  if (!dueIso) return 0;
  const d = new Date(dueIso);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.round((TODAY_REF.getTime() - d.getTime()) / (24 * 3600 * 1000));
}

function contractEndColor(endIso, status) {
  if (status === "expired" || status === "cancelled") return C.danger;
  const d = daysBetween(endIso);
  if (d === null) return C.textLight;
  if (d <= 30) return "#ea580c";
  if (d <= 90) return C.accent;
  return C.success;
}

function paymentStatusColor(status) {
  switch (status) {
    case "paid":      return C.success;
    case "due":       return C.textLight;
    case "late":      return C.accent;
    case "defaulted": return C.danger;
    default: return C.textLight;
  }
}

function fmtNum(n) { return (Number(n) || 0).toLocaleString("ar-SA"); }

/* ═════════════════════════════════════════════
   ✦ Status & enum configs
   ═════════════════════════════════════════════ */
const PROPERTY_TYPES = ["وقف", "استثماري", "شقق سكنية", "مكاتب", "محلات تجارية", "أراضٍ", "مستودعات"];
const OWNERSHIP_TYPES = ["مملوك", "موقوف", "مستأجر"];
const PROPERTY_STATUSES = [
  { key: "نشط",          color: C.success },
  { key: "قيد التطوير",   color: C.accent },
  { key: "معروض للبيع",   color: C.teal },
];

const UNIT_TYPES = ["شقة", "مكتب", "محل", "استوديو"];
const UNIT_STATUSES = [
  { key: "مؤجرة",        color: C.success },
  { key: "شاغرة",        color: C.textLight },
  { key: "تحت الصيانة",  color: C.accent },
  { key: "محجوزة",       color: C.teal },
];

const TENANT_STATUSES = [
  { key: "نشط",          color: C.success },
  { key: "سابق",         color: C.textLight },
  { key: "متعثر",        color: C.accent },
  { key: "قائمة سوداء",  color: C.danger },
];

const CONTRACT_STATUSES = [
  { key: "ساري",         color: C.success },
  { key: "منتهٍ",         color: C.danger },
  { key: "ملغى",         color: C.textLight },
  { key: "تحت التجديد",  color: C.accent },
];
const PAYMENT_SCHEDULES = ["شهري", "ربعي", "نصف سنوي", "سنوي"];

const PAYMENT_STATUSES = [
  { key: "paid",      label: "مسدد",   color: C.success },
  { key: "due",       label: "مستحق",  color: C.textLight },
  { key: "late",      label: "متأخر",  color: C.accent },
  { key: "defaulted", label: "متعثر",  color: C.danger },
];

const CORRESPONDENCE_TYPES = ["إنذار", "إخطار", "تجديد", "مطالبة", "رد", "أخرى"];
const SENT_VIA = ["بريد", "SMS", "يد", "إلكتروني"];
const DOC_TYPES = ["صك", "عقد", "إنذار", "فاتورة", "إيصال", "شهادة", "أخرى"];

/* ═════════════════════════════════════════════
   ✦ Seed Data
   ═════════════════════════════════════════════ */
/* ═════════════════════════════════════════════
   ✦ محوّلات الصفوف: snake_case (Supabase) → camelCase (الواجهة)
   ═════════════════════════════════════════════ */
const mapProperty = r => ({
  id: r.id, code: r.code || "", name: r.name || "", type: r.type || "",
  city: r.city || "", district: r.district || "", address: r.address || "",
  area: Number(r.area) || 0, totalUnits: r.total_units ?? 0,
  ownershipType: r.ownership_type || "", deedNumber: r.deed_number || "",
  deedDate: r.deed_date || "", currentValue: Number(r.current_value) || 0,
  status: r.status || "", notes: r.notes || "",
});

const mapUnit = r => ({
  id: r.id, propertyId: r.property_id || "", propertyName: r.property_name || "",
  unitNumber: r.unit_number || "", type: r.type || "", floor: r.floor ?? 0,
  area: Number(r.area) || 0, bedrooms: r.bedrooms ?? 0, bathrooms: r.bathrooms ?? 0,
  monthlyRent: Number(r.monthly_rent) || 0, status: r.status || "",
  currentTenantId: r.current_tenant_id || "", currentTenantName: r.current_tenant_name || "",
  contractEnd: r.contract_end || "", notes: r.notes || "",
});

const mapTenant = r => ({
  id: r.id, name: r.name || "", idNumber: r.id_number || "",
  nationality: r.nationality || "", phone: r.phone || "", altPhone: r.alt_phone || "",
  email: r.email || "", occupation: r.occupation || "", employer: r.employer || "",
  status: r.status || "", notes: r.notes || "",
  currentUnitId: r.current_unit_id || "", currentUnitName: r.current_unit_name || "",
  totalContracts: r.total_contracts ?? 0,
});

const mapContract = r => ({
  id: r.id, contractNumber: r.contract_number || "",
  tenantId: r.tenant_id || "", tenantName: r.tenant_name || "",
  unitId: r.unit_id || "", unitName: r.unit_name || "",
  startDate: r.start_date || "", endDate: r.end_date || "",
  durationMonths: r.duration_months ?? 0,
  monthlyRent: Number(r.monthly_rent) || 0, annualRent: Number(r.annual_rent) || 0,
  deposit: Number(r.deposit) || 0, paymentSchedule: r.payment_schedule || "",
  status: r.status || "", renewalNotes: r.renewal_notes || "",
  attachmentRef: r.attachment_ref || "",
});

const mapPayment = r => ({
  id: r.id, contractId: r.contract_id || "", contractNumber: r.contract_number || "",
  tenantName: r.tenant_name || "", dueDate: r.due_date || "",
  amount: Number(r.amount) || 0, paidDate: r.paid_date || "",
  paidAmount: Number(r.paid_amount) || 0, paymentMethod: r.payment_method || "",
  receiptNumber: r.receipt_number || "", status: r.status || "due", notes: r.notes || "",
});

const mapCorrespondence = r => ({
  id: r.id, date: r.corr_date || "", type: r.type || "",
  tenantId: r.tenant_id || "", tenantName: r.tenant_name || "",
  subject: r.subject || "", body: r.body || "", sentVia: r.sent_via || "",
  attachmentRef: r.attachment_ref || "", response: r.response || "",
  responseDate: r.response_date || "",
});

const mapDocument = r => ({
  id: r.id, type: r.type || "", propertyId: r.property_id || "",
  propertyName: r.property_name || "", tenantId: r.tenant_id || "",
  tenantName: r.tenant_name || "", contractId: r.contract_id || "",
  title: r.title || "", issueDate: r.issue_date || "", expiryDate: r.expiry_date || "",
  docNumber: r.doc_number || "", fileRef: r.file_ref || "", notes: r.notes || "",
});

const mapEndowment = r => ({
  id: r.id, propertyId: r.property_id || "", propertyName: r.property_name || "",
  founderName: r.founder_name || "", foundingDate: r.founding_date || "",
  founderConditions: r.founder_conditions || "",
  beneficiaryEntity: r.beneficiary_entity || "",
  totalRevenue: Number(r.total_revenue) || 0,
  totalExpenses: Number(r.total_expenses) || 0,
  notes: r.notes || "",
});

/* ═════════════════════════════════════════════
   ✦ سياق بيانات الأوقاف — مصدر واحد لكل التبويبات
   كل التبويبات كانت تقرأ مصفوفات على مستوى الملف؛ السياق
   يمنحها البيانات الحيّة نفسها دون تمرير 8 props عبر 13 مكوّناً.
   ═════════════════════════════════════════════ */
const WaqfDataContext = createContext(null);

function useWaqfData() {
  return useContext(WaqfDataContext) || {
    properties: [], units: [], tenants: [], contracts: [],
    payments: [], correspondence: [], documents: [], waqfFiles: [],
    reload: () => {},
  };
}
/* ═════════════════════════════════════════════
   ✦ Main page
   ═════════════════════════════════════════════ */
export default function WaqfPage({ user, perms }) {
  const [tab, setTab] = useState("overview");
  const safePerms = perms || getPerms(user);

  // بيانات حيّة من Supabase — لا بيانات وهمية
  const [properties, setProperties]         = useState([]);
  const [units, setUnits]                   = useState([]);
  const [tenants, setTenants]               = useState([]);
  const [contracts, setContracts]           = useState([]);
  const [payments, setPayments]             = useState([]);
  const [correspondence, setCorrespondence] = useState([]);
  const [documents, setDocuments]           = useState([]);
  const [endowments, setEndowments]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [loadError, setLoadError]           = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [pr, un, te, co, pa, cr, dc] = await Promise.all([
        getWaqfProperties(), getWaqfUnits(), getWaqfTenants(), getWaqfContracts(),
        getWaqfPayments(), getWaqfCorrespondence(), getWaqfDocuments(),
      ]);
      // waqf_endowments قد لا يكون منشأً بعد — لا تُسقط الموديول كله بسببه
      const en = await getWaqfEndowments().catch(() => []);
      const rows = r => (Array.isArray(r) ? r : r?.data || []);
      setProperties(rows(pr).map(mapProperty));
      setUnits(rows(un).map(mapUnit));
      setTenants(rows(te).map(mapTenant));
      setContracts(rows(co).map(mapContract));
      setPayments(rows(pa).map(mapPayment));
      setCorrespondence(rows(cr).map(mapCorrespondence));
      setDocuments(rows(dc).map(mapDocument));
      setEndowments(rows(en).map(mapEndowment));
    } catch (e) {
      setLoadError("تعذّر تحميل بيانات الأوقاف");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const ctxValue = useMemo(() => ({
    properties, units, tenants, contracts, payments, correspondence, documents,
    waqfFiles: endowments, reload: loadAll,
  }), [properties, units, tenants, contracts, payments, correspondence, documents, endowments, loadAll]);

  const tabs = [
    { key: "overview",        label: "لوحة التحكم",   icon: Activity },
    { key: "properties",      label: "العقارات",       icon: Building2 },
    { key: "units",           label: "الوحدات",        icon: Home },
    { key: "tenants",         label: "المستأجرون",      icon: Users },
    { key: "contracts",       label: "العقود",         icon: FileText },
    { key: "payments",        label: "الدفعات",        icon: Wallet },
    { key: "alerts",          label: "التنبيهات",      icon: Bell },
    { key: "correspondence",  label: "المراسلات",      icon: Mail },
    { key: "documents",       label: "المستندات",      icon: Archive },
    { key: "reports",         label: "التقارير",       icon: BarChart3 },
    { key: "waqfFile",        label: "ملف الوقف",      icon: ScrollText },
    { key: "propertyProfile", label: "صفحة العقار",   icon: Briefcase },
    { key: "tenantProfile",   label: "صفحة المستأجر", icon: User },
  ];

  const reportSources = [
    {
      label: "الدفعات", mode: "monthly", dateField: "dueDate", rows: payments,
      columns: [
        { key: "contractNumber", label: "رقم العقد" },
        { key: "tenantName", label: "المستأجر" },
        { key: "dueDate", label: "تاريخ الاستحقاق" },
        { key: "amount", label: "المبلغ" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "المراسلات", mode: "monthly", dateField: "date", rows: correspondence,
      columns: [
        { key: "type", label: "النوع" },
        { key: "tenantName", label: "المستأجر" },
        { key: "subject", label: "الموضوع" },
        { key: "sentVia", label: "قناة الإرسال" },
        { key: "date", label: "التاريخ" },
      ],
    },
    {
      label: "العقود الجديدة", mode: "monthly", dateField: "startDate", rows: contracts,
      columns: [
        { key: "contractNumber", label: "رقم العقد" },
        { key: "tenantName", label: "المستأجر" },
        { key: "unitName", label: "الوحدة" },
        { key: "startDate", label: "تاريخ البدء" },
        { key: "monthlyRent", label: "الإيجار الشهري" },
        { key: "status", label: "الحالة" },
      ],
    },
    { label: "العقارات", mode: "cumulative", rows: properties },
    { label: "الوحدات", mode: "cumulative", rows: units },
    { label: "المستأجرون", mode: "cumulative", rows: tenants },
    { label: "الوثائق", mode: "cumulative", rows: documents },
    { label: "الأوقاف", mode: "cumulative", rows: endowments },
  ];

  return (
    <div>
      <ModuleHeader icon={Landmark} title="الأوقاف والأملاك" subtitle="إدارة العقارات والوحدات والمستأجرين والعقود والريع الوقفي">
        <MonthlyReportButton moduleTitle="الأوقاف والأملاك" sources={reportSources} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loadError && (
        <Card style={{ padding: 12, marginTop: 16, background: `${C.danger}10`, border: `1px solid ${C.danger}40`, color: C.danger, fontSize: 13 }}>
          {loadError}
        </Card>
      )}

      {loading ? (
        <Card style={{ padding: 32, marginTop: 16, textAlign: "center", color: C.textLight, fontSize: 14 }}>
          جارٍ تحميل بيانات الأوقاف...
        </Card>
      ) : (
      <WaqfDataContext.Provider value={ctxValue}>
      {tab === "overview"        && <OverviewTab onJump={setTab} />}
      {tab === "properties"      && <PropertiesTab canEdit={safePerms.canEdit} />}
      {tab === "units"           && <UnitsTab canEdit={safePerms.canEdit} />}
      {tab === "tenants"         && <TenantsTab canEdit={safePerms.canEdit} />}
      {tab === "contracts"       && <ContractsTab canEdit={safePerms.canEdit} />}
      {tab === "payments"        && <PaymentsTab canEdit={safePerms.canEdit} />}
      {tab === "alerts"          && <AlertsTab onJump={setTab} />}
      {tab === "correspondence"  && <CorrespondenceTab canEdit={safePerms.canEdit} />}
      {tab === "documents"       && <DocumentsTab canEdit={safePerms.canEdit} />}
      {tab === "reports"         && <ReportsTab />}
      {tab === "waqfFile"        && <WaqfFileTab />}
      {tab === "propertyProfile" && <PropertyProfileTab />}
      {tab === "tenantProfile"   && <TenantProfileTab />}
      </WaqfDataContext.Provider>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 1) Overview Tab
   ───────────────────────────────────────────── */
function OverviewTab({ onJump }) {
  const { contracts: allContracts, payments: allPayments, properties: allProperties, tenants: allTenants, units: allUnits } = useWaqfData();
  const totalProperties = allProperties.length;
  const rentedUnits  = allUnits.filter(u => u.status === "مؤجرة").length;
  const vacantUnits  = allUnits.filter(u => u.status === "شاغرة").length;
  const monthlyRevenue = allUnits.filter(u => u.status === "مؤجرة").reduce((s, u) => s + (u.monthlyRent || 0), 0);
  const duesAmount = allPayments.filter(p => p.status === "late" || p.status === "defaulted").reduce((s, p) => s + p.amount, 0);
  const endingSoonContracts = allContracts.filter(c => {
    if (c.status !== "ساري") return false;
    const d = daysBetween(c.endDate);
    return d !== null && d >= 0 && d <= 90;
  }).length;
  const defaultedTenants = allTenants.filter(t => t.status === "متعثر" || t.status === "قائمة سوداء").length;
  const occupiableUnits = allUnits.filter(u => u.status !== "تحت الصيانة").length;
  const occupancyRate = occupiableUnits > 0 ? Math.round((rentedUnits / occupiableUnits) * 100) : 0;

  const unitStatusData = UNIT_STATUSES.map(s => ({
    name: s.key,
    value: allUnits.filter(u => u.status === s.key).length,
    color: s.color,
  }));

  const monthlyRevenueData = useMemo(() => {
    const monthsLabels = ["يول 25","أغس 25","سبت 25","أكت 25","نوف 25","ديس 25","ينا 26","فبر 26","مار 26","أبر 26","ماي 26","يون 26"];
    return monthsLabels.map((label, i) => ({
      month: label,
      value: 28000 + (i * 1800) + ((i % 3) * 2400),
    }));
  }, []);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي العقارات"  value={totalProperties}                icon={Building2}      color={C.primary} />
        <KPICard title="الوحدات المؤجرة"  value={rentedUnits}                    icon={CheckCircle}    color={C.success} />
        <KPICard title="الوحدات الشاغرة"  value={vacantUnits}                    icon={Home}           color={C.textLight} />
        <KPICard title="الإيرادات الشهرية" value={`${fmtNum(monthlyRevenue)} ر.س`} icon={DollarSign}    color={C.teal} />
        <KPICard title="المبالغ المستحقة"  value={`${fmtNum(duesAmount)} ر.س`}    icon={AlertTriangle}  color={C.accent} />
        <KPICard title="عقود قرب الانتهاء" value={endingSoonContracts}            icon={Clock}          color={C.warning} subtitle="≤ 90 يوم" />
        <KPICard title="مستأجرون متعثرون"  value={defaultedTenants}               icon={XCircle}        color={C.danger} />
        <KPICard title="نسبة الإشغال"      value={`${occupancyRate}%`}            icon={Percent}        color="#7c3aed" />
      </div>

      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitleWrap}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع الوحدات حسب الحالة</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={unitStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3} label={false} labelLine={false}>
                {unitStatusData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} وحدة`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {unitStatusData.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ ...styles.legendDot, background: d.color }} />
                <span style={styles.legendLabel}>{d.name}</span>
                <span style={styles.legendValue}>{d.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitleWrap}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>الإيرادات الشهرية (12 شهر)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${fmtNum(v)} ر.س`]}
              />
              <Bar dataKey="value" name="الإيرادات" fill={C.teal} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Bell size={18} color={C.warning} /> روابط سريعة</h4>
        <div style={styles.quickLinks}>
          <button style={styles.quickBtn} onClick={() => onJump("alerts")}>
            <AlertTriangle size={18} color={C.danger} /><span>عرض التنبيهات</span>
          </button>
          <button style={styles.quickBtn} onClick={() => onJump("contracts")}>
            <FileText size={18} color={C.accent} /><span>إدارة العقود</span>
          </button>
          <button style={styles.quickBtn} onClick={() => onJump("payments")}>
            <Receipt size={18} color={C.teal} /><span>سجل الدفعات</span>
          </button>
          <button style={styles.quickBtn} onClick={() => onJump("waqfFile")}>
            <ScrollText size={18} color="#7c3aed" /><span>ملفات الوقف</span>
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 2) Properties Tab
   ───────────────────────────────────────────── */
const INITIAL_PROPERTY = {
  code: "", name: "", type: "محلات تجارية", city: "بريدة", district: "",
  address: "", area: "", totalUnits: "", ownershipType: "مملوك",
  deedNumber: "", deedDate: "", currentValue: "", status: "نشط", notes: "",
};

function PropertiesTab({ canEdit }) {
  const { properties: allProperties, reload } = useWaqfData();
  const [rows, setRows] = useState(allProperties);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PROPERTY);
  const [selected, setSelected] = useState(null);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.code || !form.name || !form.district || !form.area) {
      showToast("الرمز والاسم والحي والمساحة مطلوبة", "error");
      return;
    }
    const payload = {
      code: form.code,
      name: form.name,
      type: form.type,
      city: form.city,
      district: form.district,
      address: form.address,
      area: Number(form.area) || 0,
      total_units: Number(form.totalUnits) || 0,
      ownership_type: form.ownershipType,
      deed_number: form.deedNumber,
      deed_date: form.deedDate || null,
      current_value: Number(form.currentValue) || 0,
      status: form.status,
      notes: form.notes,
    };
    const { data, error } = await createWaqfProperty(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      code: form.code,
      name: form.name,
      type: form.type,
      city: form.city,
      district: form.district,
      address: form.address,
      area: Number(form.area) || 0,
      totalUnits: Number(form.totalUnits) || 0,
      ownershipType: form.ownershipType,
      deedNumber: form.deedNumber,
      deedDate: form.deedDate || null,
      currentValue: Number(form.currentValue) || 0,
      status: form.status,
      notes: form.notes,
    }, ...prev]);
    setForm(INITIAL_PROPERTY);
    setShowAdd(false);
    showToast("تم إضافة العقار");
  }

  const cities = Array.from(new Set(rows.map(r => r.city)));
  const filtered = rows.filter(r => {
    if (search && !r.name.includes(search) && !r.code.includes(search)) return false;
    if (filterType !== "all" && r.type !== filterType) return false;
    if (filterCity !== "all" && r.city !== filterCity) return false;
    return true;
  });

  const columns = [
    { key: "code",         label: "الرمز",       nowrap: true },
    { key: "name",         label: "الاسم" },
    { key: "type",         label: "النوع",       nowrap: true },
    { key: "city",         label: "المدينة",     nowrap: true },
    { key: "district",     label: "الحي",        nowrap: true },
    { key: "area",         label: "م²",          nowrap: true, render: v => fmtNum(v) },
    { key: "totalUnits",   label: "الوحدات",     nowrap: true },
    { key: "ownershipType", label: "الملكية",    nowrap: true },
    { key: "currentValue", label: "القيمة",      nowrap: true, render: v => fmtNum(v) },
    { key: "status",       label: "الحالة",      nowrap: true, render: v => {
      const s = PROPERTY_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{v}</span>;
    } },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input style={styles.searchInput} placeholder="بحث بالاسم أو الرمز..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">جميع الأنواع</option>
          {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={styles.filterSelect} value={filterCity} onChange={e => setFilterCity(e.target.value)}>
          <option value="all">جميع المدن</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة عقار</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} onRowClick={setSelected} />
      </div>

      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)} width={620}>
          <div style={styles.detailGrid}>
            <div style={styles.detailRow}><strong>الرمز:</strong> {selected.code}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>المدينة/الحي:</strong> {selected.city} — {selected.district}</div>
            <div style={styles.detailRow}><strong>العنوان:</strong> {selected.address}</div>
            <div style={styles.detailRow}><strong>المساحة:</strong> {fmtNum(selected.area)} م²</div>
            <div style={styles.detailRow}><strong>عدد الوحدات:</strong> {selected.totalUnits}</div>
            <div style={styles.detailRow}><strong>نوع الملكية:</strong> {selected.ownershipType}</div>
            <div style={styles.detailRow}><strong>رقم الصك:</strong> {selected.deedNumber}</div>
            <div style={styles.detailRow}><strong>تاريخ الصك:</strong> {selected.deedDate}</div>
            <div style={styles.detailRow}><strong>القيمة الحالية:</strong> {fmtNum(selected.currentValue)} ر.س</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> {selected.status}</div>
            {selected.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة عقار جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_PROPERTY); }} width={700}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              <Field label="الرمز" required value={form.code} onChange={v => setForm({ ...form, code: v })} />
              <Field label="اسم العقار" required value={form.name} onChange={v => setForm({ ...form, name: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={PROPERTY_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="المدينة" value={form.city} onChange={v => setForm({ ...form, city: v })} />
              <Field label="الحي" required value={form.district} onChange={v => setForm({ ...form, district: v })} />
            </div>
            <Field label="العنوان" value={form.address} onChange={v => setForm({ ...form, address: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="المساحة (م²)" type="number" required value={form.area} onChange={v => setForm({ ...form, area: v })} />
              <Field label="عدد الوحدات" type="number" value={form.totalUnits} onChange={v => setForm({ ...form, totalUnits: v })} />
              <Field label="القيمة الحالية" type="number" value={form.currentValue} onChange={v => setForm({ ...form, currentValue: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="نوع الملكية" type="select" options={OWNERSHIP_TYPES} value={form.ownershipType} onChange={v => setForm({ ...form, ownershipType: v })} />
              <Field label="رقم الصك" value={form.deedNumber} onChange={v => setForm({ ...form, deedNumber: v })} />
              <Field label="تاريخ الصك" type="date" value={form.deedDate} onChange={v => setForm({ ...form, deedDate: v })} />
            </div>
            <Field label="الحالة" type="select" options={PROPERTY_STATUSES.map(s => s.key)} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PROPERTY); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 3) Units Tab
   ───────────────────────────────────────────── */
const INITIAL_UNIT = {
  propertyId: "", unitNumber: "", type: "شقة", floor: "", area: "",
  bedrooms: "", bathrooms: "", monthlyRent: "", status: "شاغرة", notes: "",
};

function UnitsTab({ canEdit }) {
  const { properties: allProperties, units: allUnits, reload } = useWaqfData();
  const [rows, setRows] = useState(allUnits);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProperty, setFilterProperty] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_UNIT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.propertyId || !form.unitNumber || !form.area) {
      showToast("العقار ورقم الوحدة والمساحة مطلوبة", "error");
      return;
    }
    const prop = allProperties.find(p => p.id === form.propertyId);
    const payload = {
      property_id: form.propertyId,
      property_name: prop?.name || "—",
      unit_number: form.unitNumber,
      type: form.type,
      floor: Number(form.floor) || 0,
      area: Number(form.area) || 0,
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      monthly_rent: Number(form.monthlyRent) || 0,
      status: form.status,
      current_tenant_id: null,
      current_tenant_name: null,
      contract_end: null,
      notes: form.notes,
    };
    const { data, error } = await createWaqfUnit(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      propertyId: form.propertyId,
      propertyName: prop?.name || "—",
      unitNumber: form.unitNumber,
      type: form.type,
      floor: Number(form.floor) || 0,
      area: Number(form.area) || 0,
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      monthlyRent: Number(form.monthlyRent) || 0,
      status: form.status,
      currentTenantId: null,
      currentTenantName: null,
      contractEnd: null,
      notes: form.notes,
    }, ...prev]);
    setForm(INITIAL_UNIT);
    setShowAdd(false);
    showToast("تم إضافة الوحدة");
  }

  const filtered = rows.filter(r => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (filterProperty !== "all" && r.propertyId !== filterProperty) return false;
    return true;
  });

  const columns = [
    { key: "unitNumber",       label: "الوحدة",       nowrap: true },
    { key: "propertyName",     label: "العقار" },
    { key: "type",             label: "النوع",        nowrap: true },
    { key: "floor",            label: "الدور",        nowrap: true },
    { key: "area",             label: "م²",          nowrap: true },
    { key: "bedrooms",         label: "غرف",          nowrap: true },
    { key: "bathrooms",        label: "حمام",         nowrap: true },
    { key: "monthlyRent",      label: "الإيجار/شهر",  nowrap: true, render: v => fmtNum(v) },
    { key: "currentTenantName", label: "المستأجر",     render: v => v || "—" },
    { key: "contractEnd",      label: "نهاية العقد",   nowrap: true, render: v => v || "—" },
    { key: "status",           label: "الحالة",       nowrap: true, render: v => {
      const s = UNIT_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{v}</span>;
    } },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {UNIT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
        </select>
        <select style={styles.filterSelect} value={filterProperty} onChange={e => setFilterProperty(e.target.value)}>
          <option value="all">جميع العقارات</option>
          {allProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة وحدة</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة وحدة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_UNIT); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
              <Field label="العقار" type="select" required options={allProperties.map(p => ({ value: p.id, label: p.name }))} value={form.propertyId} onChange={v => setForm({ ...form, propertyId: v })} />
              <Field label="رقم الوحدة" required value={form.unitNumber} onChange={v => setForm({ ...form, unitNumber: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={UNIT_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="الدور" type="number" value={form.floor} onChange={v => setForm({ ...form, floor: v })} />
              <Field label="المساحة (م²)" type="number" required value={form.area} onChange={v => setForm({ ...form, area: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="غرف النوم" type="number" value={form.bedrooms} onChange={v => setForm({ ...form, bedrooms: v })} />
              <Field label="دورات المياه" type="number" value={form.bathrooms} onChange={v => setForm({ ...form, bathrooms: v })} />
              <Field label="الإيجار الشهري" type="number" value={form.monthlyRent} onChange={v => setForm({ ...form, monthlyRent: v })} />
            </div>
            <Field label="الحالة" type="select" options={UNIT_STATUSES.map(s => s.key)} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_UNIT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 4) Tenants Tab
   ───────────────────────────────────────────── */
const INITIAL_TENANT = {
  name: "", idNumber: "", nationality: "سعودي", phone: "", altPhone: "",
  email: "", occupation: "", employer: "", status: "نشط", notes: "",
};

function TenantsTab({ canEdit }) {
  const { tenants: allTenants, reload } = useWaqfData();
  const [rows, setRows] = useState(allTenants);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_TENANT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.name || !form.idNumber || !form.phone) {
      showToast("الاسم والهوية والهاتف مطلوبة", "error");
      return;
    }
    const payload = {
      name: form.name,
      id_number: form.idNumber,
      nationality: form.nationality,
      phone: form.phone,
      alt_phone: form.altPhone,
      email: form.email,
      occupation: form.occupation,
      employer: form.employer,
      status: form.status,
      notes: form.notes,
      current_unit_id: null,
      current_unit_name: null,
      total_contracts: 0,
    };
    const { data, error } = await createWaqfTenant(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      ...form,
      currentUnitId: null,
      currentUnitName: null,
      totalContracts: 0,
    }, ...prev]);
    setForm(INITIAL_TENANT);
    setShowAdd(false);
    showToast("تم إضافة المستأجر");
  }

  const filtered = rows.filter(r => {
    if (search && !r.name.includes(search) && !r.idNumber.includes(search) && !(r.phone || "").includes(search)) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    return true;
  });

  const columns = [
    { key: "name",             label: "الاسم" },
    { key: "idNumber",         label: "الهوية",      nowrap: true },
    { key: "nationality",      label: "الجنسية",     nowrap: true },
    { key: "phone",            label: "الهاتف",      nowrap: true },
    { key: "email",            label: "البريد" },
    { key: "occupation",       label: "المهنة",      nowrap: true },
    { key: "currentUnitName",  label: "الوحدة",       render: v => v || "—" },
    { key: "totalContracts",   label: "العقود",      nowrap: true },
    { key: "status",           label: "الحالة",      nowrap: true, render: v => {
      const s = TENANT_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{v}</span>;
    } },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input style={styles.searchInput} placeholder="بحث بالاسم/الهوية/الهاتف..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {TENANT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة مستأجر</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة مستأجر جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_TENANT); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="رقم الهوية" required value={form.idNumber} onChange={v => setForm({ ...form, idNumber: v })} />
              <Field label="الجنسية" value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="الحالة" type="select" options={TENANT_STATUSES.map(s => s.key)} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الهاتف" required value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="هاتف بديل" value={form.altPhone} onChange={v => setForm({ ...form, altPhone: v })} />
            </div>
            <Field label="البريد الإلكتروني" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المهنة" value={form.occupation} onChange={v => setForm({ ...form, occupation: v })} />
              <Field label="جهة العمل" value={form.employer} onChange={v => setForm({ ...form, employer: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_TENANT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 5) Contracts Tab
   ───────────────────────────────────────────── */
const INITIAL_CONTRACT = {
  contractNumber: "", tenantId: "", unitId: "",
  startDate: "", endDate: "", durationMonths: "",
  monthlyRent: "", deposit: "", paymentSchedule: "شهري",
  status: "ساري", renewalNotes: "", attachmentRef: "",
};

function ContractsTab({ canEdit }) {
  const { contracts: allContracts, tenants: allTenants, units: allUnits, reload } = useWaqfData();
  const [rows, setRows] = useState(allContracts);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_CONTRACT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.contractNumber || !form.tenantId || !form.unitId || !form.startDate || !form.endDate || !form.monthlyRent) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const tenant = allTenants.find(t => t.id === form.tenantId);
    const unit = allUnits.find(u => u.id === form.unitId);
    const rent = Number(form.monthlyRent) || 0;
    const unitName = unit ? `${unit.propertyName} — ${unit.unitNumber}` : "—";
    const payload = {
      contract_number: form.contractNumber,
      tenant_id: form.tenantId,
      tenant_name: tenant?.name || "—",
      unit_id: form.unitId,
      unit_name: unitName,
      start_date: form.startDate || null,
      end_date: form.endDate || null,
      duration_months: Number(form.durationMonths) || 0,
      monthly_rent: rent,
      annual_rent: rent * 12,
      deposit: Number(form.deposit) || 0,
      payment_schedule: form.paymentSchedule,
      status: form.status,
      renewal_notes: form.renewalNotes,
      attachment_ref: form.attachmentRef,
    };
    const { data, error } = await createWaqfContract(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      contractNumber: form.contractNumber,
      tenantId: form.tenantId,
      tenantName: tenant?.name || "—",
      unitId: form.unitId,
      unitName,
      startDate: form.startDate,
      endDate: form.endDate,
      durationMonths: Number(form.durationMonths) || 0,
      monthlyRent: rent,
      annualRent: rent * 12,
      deposit: Number(form.deposit) || 0,
      paymentSchedule: form.paymentSchedule,
      status: form.status,
      renewalNotes: form.renewalNotes,
      attachmentRef: form.attachmentRef,
    }, ...prev]);
    setForm(INITIAL_CONTRACT);
    setShowAdd(false);
    showToast("تم إضافة العقد");
  }

  const filtered = rows.filter(r => filterStatus === "all" || r.status === filterStatus);

  const columns = [
    { key: "contractNumber",  label: "رقم العقد",     nowrap: true },
    { key: "tenantName",      label: "المستأجر" },
    { key: "unitName",        label: "الوحدة" },
    { key: "startDate",       label: "البداية",       nowrap: true },
    { key: "endDate",         label: "النهاية",       nowrap: true, render: (v, row) => {
      const color = contractEndColor(v, row.status);
      const d = daysBetween(v);
      const suffix = (row.status === "ساري" && d !== null && d >= 0) ? ` (${d} يوم)` : "";
      return <span style={{ color, fontWeight: 600 }}>{v}{suffix}</span>;
    } },
    { key: "monthlyRent",     label: "إيجار/شهر",     nowrap: true, render: v => fmtNum(v) },
    { key: "annualRent",      label: "إيجار/سنة",     nowrap: true, render: v => fmtNum(v) },
    { key: "deposit",         label: "التأمين",       nowrap: true, render: v => fmtNum(v) },
    { key: "paymentSchedule", label: "الجدول",        nowrap: true },
    { key: "status",          label: "الحالة",        nowrap: true, render: v => {
      const s = CONTRACT_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{v}</span>;
    } },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {CONTRACT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>عقد جديد</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة عقد إيجار" onClose={() => { setShowAdd(false); setForm(INITIAL_CONTRACT); }} width={700}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="رقم العقد" required value={form.contractNumber} onChange={v => setForm({ ...form, contractNumber: v })} />
              <Field label="المستأجر" type="select" required options={allTenants.map(t => ({ value: t.id, label: t.name }))} value={form.tenantId} onChange={v => setForm({ ...form, tenantId: v })} />
              <Field label="الوحدة" type="select" required options={allUnits.map(u => ({ value: u.id, label: `${u.propertyName} — ${u.unitNumber}` }))} value={form.unitId} onChange={v => setForm({ ...form, unitId: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" required value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" required value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
              <Field label="المدة (شهر)" type="number" value={form.durationMonths} onChange={v => setForm({ ...form, durationMonths: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الإيجار الشهري" type="number" required value={form.monthlyRent} onChange={v => setForm({ ...form, monthlyRent: v })} />
              <Field label="التأمين" type="number" value={form.deposit} onChange={v => setForm({ ...form, deposit: v })} />
              <Field label="جدول الدفع" type="select" options={PAYMENT_SCHEDULES} value={form.paymentSchedule} onChange={v => setForm({ ...form, paymentSchedule: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              <Field label="الحالة" type="select" options={CONTRACT_STATUSES.map(s => s.key)} value={form.status} onChange={v => setForm({ ...form, status: v })} />
              <Field label="ملاحظات التجديد" value={form.renewalNotes} onChange={v => setForm({ ...form, renewalNotes: v })} />
            </div>
            <Field label="مرجع المرفق" value={form.attachmentRef} onChange={v => setForm({ ...form, attachmentRef: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_CONTRACT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 6) Payments Tab
   ───────────────────────────────────────────── */
const INITIAL_PAYMENT = {
  contractId: "", dueDate: "", amount: "", paidDate: "", paidAmount: "",
  paymentMethod: "تحويل بنكي", receiptNumber: "", status: "due", notes: "",
};

function PaymentsTab({ canEdit }) {
  const { contracts: allContracts, payments: allPayments, reload } = useWaqfData();
  const [rows, setRows] = useState(allPayments);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PAYMENT);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.contractId || !form.dueDate || !form.amount) {
      showToast("العقد وتاريخ الاستحقاق والمبلغ مطلوبة", "error");
      return;
    }
    const contract = allContracts.find(c => c.id === form.contractId);
    const payload = {
      contract_id: form.contractId,
      contract_number: contract?.contractNumber || "—",
      tenant_name: contract?.tenantName || "—",
      due_date: form.dueDate || null,
      amount: Number(form.amount) || 0,
      paid_date: form.paidDate || null,
      paid_amount: Number(form.paidAmount) || 0,
      payment_method: form.paymentMethod,
      receipt_number: form.receiptNumber,
      status: form.status,
      notes: form.notes,
    };
    const { data, error } = await createWaqfPayment(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      contractId: form.contractId,
      contractNumber: contract?.contractNumber || "—",
      tenantName: contract?.tenantName || "—",
      dueDate: form.dueDate,
      amount: Number(form.amount) || 0,
      paidDate: form.paidDate,
      paidAmount: Number(form.paidAmount) || 0,
      paymentMethod: form.paymentMethod,
      receiptNumber: form.receiptNumber,
      status: form.status,
      notes: form.notes,
    }, ...prev]);
    setForm(INITIAL_PAYMENT);
    setShowAdd(false);
    showToast("تم تسجيل الدفعة");
  }

  const filtered = rows.filter(r => filterStatus === "all" || r.status === filterStatus);

  const columns = [
    { key: "contractNumber", label: "رقم العقد",      nowrap: true },
    { key: "tenantName",     label: "المستأجر" },
    { key: "dueDate",        label: "تاريخ الاستحقاق", nowrap: true },
    { key: "amount",         label: "المبلغ",          nowrap: true, render: v => fmtNum(v) },
    { key: "paidDate",       label: "تاريخ السداد",    nowrap: true, render: v => v || "—" },
    { key: "paidAmount",     label: "المسدد",          nowrap: true, render: v => v ? fmtNum(v) : "—" },
    { key: "paymentMethod",  label: "طريقة الدفع",     nowrap: true, render: v => v || "—" },
    { key: "receiptNumber",  label: "رقم الإيصال",     nowrap: true, render: v => v || "—" },
    { key: "status",         label: "الحالة",          nowrap: true, render: (v, row) => {
      const s = PAYMENT_STATUSES.find(x => x.key === v);
      const overdue = (v === "late" || v === "defaulted") ? daysOverdue(row.dueDate) : null;
      const color = paymentStatusColor(v);
      const lbl = s?.label || v;
      const suffix = overdue !== null && overdue > 0 ? ` (${overdue} يوم)` : "";
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}18`, color }}>{lbl}{suffix}</span>;
    } },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {PAYMENT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>تسجيل دفعة</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="تسجيل دفعة" onClose={() => { setShowAdd(false); setForm(INITIAL_PAYMENT); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العقد" type="select" required options={allContracts.map(c => ({ value: c.id, label: `${c.contractNumber} — ${c.tenantName}` }))} value={form.contractId} onChange={v => setForm({ ...form, contractId: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ الاستحقاق" type="date" required value={form.dueDate} onChange={v => setForm({ ...form, dueDate: v })} />
              <Field label="المبلغ" type="number" required value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
              <Field label="الحالة" type="select" options={PAYMENT_STATUSES.map(s => ({ value: s.key, label: s.label }))} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ السداد" type="date" value={form.paidDate} onChange={v => setForm({ ...form, paidDate: v })} />
              <Field label="المبلغ المسدد" type="number" value={form.paidAmount} onChange={v => setForm({ ...form, paidAmount: v })} />
              <Field label="رقم الإيصال" value={form.receiptNumber} onChange={v => setForm({ ...form, receiptNumber: v })} />
            </div>
            <Field label="طريقة الدفع" type="select" options={["تحويل بنكي", "شيك", "نقدي", "بطاقة ائتمان", "سداد"]} value={form.paymentMethod} onChange={v => setForm({ ...form, paymentMethod: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PAYMENT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 7) Alerts Tab
   ───────────────────────────────────────────── */
function AlertsTab({ onJump }) {
  const { contracts: allContracts, payments: allPayments } = useWaqfData();
  const buckets = useMemo(() => {
    const activeContracts = allContracts.filter(c => c.status === "ساري");
    const contracts90 = activeContracts.filter(c => {
      const d = daysBetween(c.endDate);
      return d !== null && d > 60 && d <= 90;
    });
    const contracts60 = activeContracts.filter(c => {
      const d = daysBetween(c.endDate);
      return d !== null && d > 30 && d <= 60;
    });
    const contracts30 = activeContracts.filter(c => {
      const d = daysBetween(c.endDate);
      return d !== null && d >= 0 && d <= 30;
    });
    const paymentsLate = allPayments.filter(p => {
      const od = daysOverdue(p.dueDate);
      return p.status !== "paid" && od >= 7 && od <= 30;
    });
    const paymentsDefaulted = allPayments.filter(p => {
      const od = daysOverdue(p.dueDate);
      return p.status !== "paid" && od > 30;
    });
    return { contracts90, contracts60, contracts30, paymentsLate, paymentsDefaulted };
  }, [allContracts, allPayments]);

  const cards = [
    { key: "c90", title: "عقود تنتهي خلال 90 يوم", icon: Clock,        color: C.accent,  rows: buckets.contracts90, kind: "contract", jump: "contracts" },
    { key: "c60", title: "عقود تنتهي خلال 60 يوم", icon: Clock,        color: "#ea580c", rows: buckets.contracts60, kind: "contract", jump: "contracts" },
    { key: "c30", title: "عقود تنتهي خلال 30 يوم", icon: AlertTriangle, color: C.danger,  rows: buckets.contracts30, kind: "contract", jump: "contracts" },
    { key: "pl",  title: "دفعات متأخرة (7-30 يوم)", icon: AlertTriangle, color: C.accent,  rows: buckets.paymentsLate, kind: "payment", jump: "payments" },
    { key: "pd",  title: "دفعات متعثرة (> 30 يوم)", icon: XCircle,      color: C.danger,  rows: buckets.paymentsDefaulted, kind: "payment", jump: "payments" },
  ];

  const totalAlerts = cards.reduce((s, c) => s + c.rows.length, 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي التنبيهات" value={totalAlerts} icon={Bell} color={C.warning} />
        {cards.map(c => (
          <KPICard key={c.key} title={c.title} value={c.rows.length} icon={c.icon} color={c.color} />
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
        {cards.map(card => (
          <Card key={card.key} style={{ padding: 18, borderRight: `4px solid ${card.color}` }}>
            <div style={styles.alertHeader}>
              <div style={styles.chartTitleWrap}>
                <card.icon size={20} color={card.color} />
                <span style={{ ...styles.chartTitle, color: card.color }}>{card.title}</span>
                <span style={{ ...styles.alertCount, background: `${card.color}18`, color: card.color }}>{card.rows.length}</span>
              </div>
              <Btn variant="ghost" size="sm" icon={Eye} onClick={() => onJump(card.jump)}>عرض</Btn>
            </div>

            {card.rows.length === 0 ? (
              <p style={{ color: C.textLight, fontSize: 13, margin: "8px 0 0" }}>لا توجد تنبيهات في هذه الفئة</p>
            ) : (
              <div style={{ marginTop: 10 }}>
                {card.kind === "contract" ? (
                  <DataTable
                    columns={[
                      { key: "contractNumber", label: "رقم العقد", nowrap: true },
                      { key: "tenantName",     label: "المستأجر" },
                      { key: "unitName",       label: "الوحدة" },
                      { key: "endDate",        label: "نهاية العقد", nowrap: true, render: (v, row) => {
                        const d = daysBetween(v);
                        const color = contractEndColor(v, row.status);
                        return <span style={{ color, fontWeight: 600 }}>{v} ({d} يوم)</span>;
                      } },
                    ]}
                    data={card.rows}
                  />
                ) : (
                  <DataTable
                    columns={[
                      { key: "contractNumber", label: "رقم العقد",       nowrap: true },
                      { key: "tenantName",     label: "المستأجر" },
                      { key: "dueDate",        label: "تاريخ الاستحقاق", nowrap: true },
                      { key: "amount",         label: "المبلغ",          nowrap: true, render: v => fmtNum(v) },
                      { key: "_overdue",       label: "تأخر",            nowrap: true, render: (_, row) => <span style={{ color: card.color, fontWeight: 700 }}>{daysOverdue(row.dueDate)} يوم</span> },
                    ]}
                    data={card.rows}
                  />
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 8) Correspondence Tab
   ───────────────────────────────────────────── */
const INITIAL_CORRESPONDENCE = {
  date: "", type: "إخطار", tenantId: "", subject: "", body: "",
  sentVia: "بريد", attachmentRef: "", response: "", responseDate: "",
};

function CorrespondenceTab({ canEdit }) {
  const { correspondence: allCorrespondence, tenants: allTenants, reload } = useWaqfData();
  const [rows, setRows] = useState(allCorrespondence);
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_CORRESPONDENCE);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.date || !form.tenantId || !form.subject) {
      showToast("التاريخ والمستأجر والموضوع مطلوبة", "error");
      return;
    }
    const tenant = allTenants.find(t => t.id === form.tenantId);
    const payload = {
      corr_date: form.date || null,
      type: form.type,
      tenant_id: form.tenantId,
      tenant_name: tenant?.name || "—",
      subject: form.subject,
      body: form.body,
      sent_via: form.sentVia,
      attachment_ref: form.attachmentRef,
      response: form.response,
      response_date: form.responseDate || null,
    };
    const { data, error } = await createWaqfCorrespondence(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      ...form,
      tenantName: tenant?.name || "—",
    }, ...prev]);
    setForm(INITIAL_CORRESPONDENCE);
    setShowAdd(false);
    showToast("تم تسجيل المراسلة");
  }

  const filtered = rows.filter(r => filterType === "all" || r.type === filterType);

  const columns = [
    { key: "date",         label: "التاريخ",   nowrap: true },
    { key: "type",         label: "النوع",     nowrap: true },
    { key: "tenantName",   label: "المستأجر" },
    { key: "subject",      label: "الموضوع" },
    { key: "sentVia",      label: "أُرسل عبر", nowrap: true },
    { key: "response",     label: "الرد",      render: v => v || "—" },
    { key: "responseDate", label: "تاريخ الرد", nowrap: true, render: v => v || "—" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <select style={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">جميع الأنواع</option>
          {CORRESPONDENCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>مراسلة جديدة</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة مراسلة" onClose={() => { setShowAdd(false); setForm(INITIAL_CORRESPONDENCE); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="النوع" type="select" options={CORRESPONDENCE_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="أُرسل عبر" type="select" options={SENT_VIA} value={form.sentVia} onChange={v => setForm({ ...form, sentVia: v })} />
            </div>
            <Field label="المستأجر" type="select" required options={allTenants.map(t => ({ value: t.id, label: t.name }))} value={form.tenantId} onChange={v => setForm({ ...form, tenantId: v })} />
            <Field label="الموضوع" required value={form.subject} onChange={v => setForm({ ...form, subject: v })} />
            <Field label="نص المراسلة" type="textarea" rows={3} value={form.body} onChange={v => setForm({ ...form, body: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
              <Field label="الرد" value={form.response} onChange={v => setForm({ ...form, response: v })} />
              <Field label="تاريخ الرد" type="date" value={form.responseDate} onChange={v => setForm({ ...form, responseDate: v })} />
            </div>
            <Field label="مرجع المرفق" value={form.attachmentRef} onChange={v => setForm({ ...form, attachmentRef: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Send} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_CORRESPONDENCE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 9) Documents Tab
   ───────────────────────────────────────────── */
const INITIAL_DOC = {
  type: "عقد", propertyId: "", tenantId: "", contractId: "",
  title: "", issueDate: "", expiryDate: "", docNumber: "",
  fileRef: "", notes: "",
};

function DocumentsTab({ canEdit }) {
  const { contracts: allContracts, documents: allDocuments, properties: allProperties, tenants: allTenants, reload } = useWaqfData();
  const [rows, setRows] = useState(allDocuments);
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_DOC);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.type || !form.title || !form.issueDate) {
      showToast("النوع والعنوان وتاريخ الإصدار مطلوبة", "error");
      return;
    }
    const prop = allProperties.find(p => p.id === form.propertyId);
    const tenant = allTenants.find(t => t.id === form.tenantId);
    const payload = {
      type: form.type,
      property_id: form.propertyId || null,
      property_name: prop?.name || null,
      tenant_id: form.tenantId || null,
      tenant_name: tenant?.name || null,
      contract_id: form.contractId || null,
      title: form.title,
      issue_date: form.issueDate || null,
      expiry_date: form.expiryDate || null,
      doc_number: form.docNumber,
      file_ref: form.fileRef,
      notes: form.notes,
    };
    const { data, error } = await createWaqfDocument(payload);
    if (error) { showToast(error, "error"); return; }
    reload();   // حدّث المخزن المشترك حتى لا يختفي الصف عند تبديل التبويب
    setRows(prev => [{
      id: data?.id,
      ...form,
      propertyName: prop?.name || null,
      tenantName: tenant?.name || null,
    }, ...prev]);
    setForm(INITIAL_DOC);
    setShowAdd(false);
    showToast("تم إضافة المستند");
  }

  const filtered = rows.filter(r => filterType === "all" || r.type === filterType);

  const columns = [
    { key: "type",         label: "النوع",        nowrap: true },
    { key: "title",        label: "العنوان" },
    { key: "docNumber",    label: "الرقم",        nowrap: true },
    { key: "propertyName", label: "العقار",       render: v => v || "—" },
    { key: "tenantName",   label: "المستأجر",     render: v => v || "—" },
    { key: "issueDate",    label: "الإصدار",      nowrap: true },
    { key: "expiryDate",   label: "الانتهاء",     nowrap: true, render: v => v || "—" },
    { key: "fileRef",      label: "المرفق",       nowrap: true, render: v => v || "—" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <select style={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">جميع الأنواع</option>
          {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة مستند</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة مستند" onClose={() => { setShowAdd(false); setForm(INITIAL_DOC); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" required options={DOC_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="العنوان" required value={form.title} onChange={v => setForm({ ...form, title: v })} />
              <Field label="رقم المستند" value={form.docNumber} onChange={v => setForm({ ...form, docNumber: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="العقار" type="select" options={[{ value: "", label: "—" }, ...allProperties.map(p => ({ value: p.id, label: p.name }))]} value={form.propertyId} onChange={v => setForm({ ...form, propertyId: v })} />
              <Field label="المستأجر" type="select" options={[{ value: "", label: "—" }, ...allTenants.map(t => ({ value: t.id, label: t.name }))]} value={form.tenantId} onChange={v => setForm({ ...form, tenantId: v })} />
              <Field label="العقد" type="select" options={[{ value: "", label: "—" }, ...allContracts.map(c => ({ value: c.id, label: c.contractNumber }))]} value={form.contractId} onChange={v => setForm({ ...form, contractId: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ الإصدار" type="date" required value={form.issueDate} onChange={v => setForm({ ...form, issueDate: v })} />
              <Field label="تاريخ الانتهاء" type="date" value={form.expiryDate} onChange={v => setForm({ ...form, expiryDate: v })} />
              <Field label="المرفق" value={form.fileRef} onChange={v => setForm({ ...form, fileRef: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_DOC); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 10) Reports Tab
   ───────────────────────────────────────────── */
function ReportsTab() {
  const [showToast, ToastView] = useToast();
  const reports = [
    { key: "occupancy",   title: "تقرير الإشغال",         icon: Percent,        color: "#7c3aed", desc: "نسبة الإشغال للوحدات حسب العقار والنوع" },
    { key: "collection",  title: "تقرير التحصيل الشهري", icon: DollarSign,     color: C.success, desc: "ملخص الدفعات المحصلة شهرياً" },
    { key: "overdue",     title: "تقرير المتأخرات",       icon: AlertTriangle,  color: C.accent,  desc: "قائمة الدفعات المتأخرة والمتعثرة" },
    { key: "expired",     title: "تقرير العقود المنتهية", icon: FileCheck,      color: C.danger,  desc: "العقود المنتهية وقرب الانتهاء" },
    { key: "waqfYield",   title: "تقرير ريع الأوقاف",     icon: ScrollText,     color: C.teal,    desc: "حركة ريع الأوقاف وصافي الرصيد" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {reports.map(r => (
          <Card key={r.key} style={{ padding: 18, borderTop: `4px solid ${r.color}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: `${r.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <r.icon size={22} color={r.color} />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{r.title}</h4>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>{r.desc}</p>
              </div>
            </div>
            <Btn icon={BarChart3} variant="ghost" size="sm" onClick={() => showToast(`جاري توليد ${r.title}...`)}>توليد التقرير</Btn>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 11) Waqf File Tab
   ───────────────────────────────────────────── */
function WaqfFileTab() {
  const { contracts: allContracts, payments: allPayments, units: allUnits, waqfFiles: allWaqfFiles } = useWaqfData();
  const [selected, setSelected] = useState(null);

  const enriched = allWaqfFiles.map(w => ({
    ...w,
    netBalance: (Number(w.totalRevenue) || 0) - (Number(w.totalExpenses) || 0),
  }));

  const totalRevenue = enriched.reduce((s, w) => s + w.totalRevenue, 0);
  const totalExpenses = enriched.reduce((s, w) => s + w.totalExpenses, 0);
  const totalNet = totalRevenue - totalExpenses;

  const columns = [
    { key: "propertyName",      label: "اسم الوقف" },
    { key: "founderName",       label: "الواقف" },
    { key: "foundingDate",      label: "تاريخ الوقفية", nowrap: true },
    { key: "beneficiaryEntity", label: "جهة الصرف" },
    { key: "totalRevenue",      label: "الريع التراكمي", nowrap: true, render: v => <span style={{ color: C.success, fontWeight: 600 }}>{fmtNum(v)}</span> },
    { key: "totalExpenses",     label: "المصروف",       nowrap: true, render: v => <span style={{ color: C.danger,  fontWeight: 600 }}>{fmtNum(v)}</span> },
    { key: "netBalance",        label: "المتبقي",       nowrap: true, render: v => <span style={{ color: v >= 0 ? C.teal : C.danger, fontWeight: 700 }}>{fmtNum(v)}</span> },
  ];

  const payments = allPayments;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="عدد الأوقاف"     value={enriched.length}        icon={ScrollText} color="#7c3aed" />
        <KPICard title="إجمالي الريع"     value={fmtNum(totalRevenue)}   icon={TrendingUp} color={C.success} />
        <KPICard title="إجمالي المصروف"   value={fmtNum(totalExpenses)}  icon={DollarSign} color={C.danger} />
        <KPICard title="صافي الرصيد"      value={fmtNum(totalNet)}       icon={Wallet}     color={C.teal} />
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={enriched} onRowClick={setSelected} />
      </div>

      {selected && (
        <Modal title={`ملف الوقف — ${selected.propertyName}`} onClose={() => setSelected(null)} width={720}>
          <div style={styles.detailGrid}>
            <div style={styles.detailRow}><strong>الواقف:</strong> {selected.founderName}</div>
            <div style={styles.detailRow}><strong>تاريخ الوقفية:</strong> {selected.foundingDate}</div>
            <div style={styles.detailRow}><strong>الشرط:</strong> {selected.founderConditions}</div>
            <div style={styles.detailRow}><strong>جهة الصرف:</strong> {selected.beneficiaryEntity}</div>
            <div style={styles.detailRow}><strong>الريع التراكمي:</strong> <span style={{ color: C.success, fontWeight: 700 }}>{fmtNum(selected.totalRevenue)} ر.س</span></div>
            <div style={styles.detailRow}><strong>المصروف:</strong> <span style={{ color: C.danger, fontWeight: 700 }}>{fmtNum(selected.totalExpenses)} ر.س</span></div>
            <div style={styles.detailRow}><strong>صافي الرصيد:</strong> <span style={{ color: selected.netBalance >= 0 ? C.teal : C.danger, fontWeight: 700 }}>{fmtNum(selected.netBalance)} ر.س</span></div>
          </div>

          <h4 style={{ ...styles.sectionTitle, marginTop: 20 }}>
            <Receipt size={16} color={C.teal} /> آخر حركات الدفع المرتبطة
          </h4>
          <DataTable
            columns={[
              { key: "dueDate",   label: "الاستحقاق", nowrap: true },
              { key: "tenantName", label: "المستأجر" },
              { key: "amount",    label: "المبلغ", nowrap: true, render: v => fmtNum(v) },
              { key: "status",    label: "الحالة", nowrap: true, render: v => {
                const s = PAYMENT_STATUSES.find(x => x.key === v);
                return <span style={{ color: paymentStatusColor(v), fontWeight: 600 }}>{s?.label || v}</span>;
              } },
            ]}
            data={payments.filter(p => {
              const c = allContracts.find(ct => ct.id === p.contractId);
              const u = allUnits.find(uu => uu.id === c?.unitId);
              return u?.propertyId === selected.propertyId;
            }).slice(0, 8)}
          />
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 12) Property Profile Tab
   ───────────────────────────────────────────── */
function PropertyProfileTab() {
  const { contracts: allContracts, documents: allDocuments, payments: allPayments, properties: allProperties, tenants: allTenants, units: allUnits } = useWaqfData();
  const [selectedId, setSelectedId] = useState(allProperties[0]?.id || "");
  const property = allProperties.find(p => p.id === selectedId);

  const units = property ? allUnits.filter(u => u.propertyId === property.id) : [];
  const unitIds = units.map(u => u.id);
  const tenants = allTenants.filter(t => unitIds.includes(t.currentUnitId));
  const contracts = allContracts.filter(c => unitIds.includes(c.unitId));
  const contractIds = contracts.map(c => c.id);
  const payments = allPayments.filter(p => contractIds.includes(p.contractId));
  const docs = property ? allDocuments.filter(d => d.propertyId === property.id) : [];

  const monthlyBars = useMemo(() => {
    const buckets = {};
    payments.filter(p => p.status === "paid").forEach(p => {
      const m = (p.paidDate || p.dueDate || "").slice(0, 7);
      if (!m) return;
      buckets[m] = (buckets[m] || 0) + (p.paidAmount || p.amount);
    });
    return Object.entries(buckets).sort(([a],[b]) => a.localeCompare(b)).slice(-12).map(([month, value]) => ({ month, value }));
  }, [payments]);

  if (!property) {
    return (
      <div style={{ marginTop: 16 }}>
        <EmptyState title="اختر عقاراً" subtitle="استخدم القائمة المنسدلة أعلاه لعرض ملف العقار" />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <Field
          label="اختر العقار"
          type="select"
          options={allProperties.map(p => ({ value: p.id, label: p.name }))}
          value={selectedId}
          onChange={setSelectedId}
        />
      </Card>

      <div style={styles.profileGrid}>
        <Card style={{ padding: 18 }}>
          <h4 style={styles.sectionTitle}><Building2 size={16} color={C.primary} /> بيانات العقار</h4>
          <div style={styles.detailGrid}>
            <div style={styles.detailRow}><strong>الرمز:</strong> {property.code}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {property.type}</div>
            <div style={styles.detailRow}><strong>المدينة/الحي:</strong> {property.city} — {property.district}</div>
            <div style={styles.detailRow}><strong>العنوان:</strong> {property.address}</div>
            <div style={styles.detailRow}><strong>المساحة:</strong> {fmtNum(property.area)} م²</div>
            <div style={styles.detailRow}><strong>عدد الوحدات:</strong> {property.totalUnits}</div>
            <div style={styles.detailRow}><strong>الملكية:</strong> {property.ownershipType}</div>
            <div style={styles.detailRow}><strong>القيمة:</strong> {fmtNum(property.currentValue)} ر.س</div>
            <div style={styles.detailRow}><strong>الصك:</strong> {property.deedNumber} ({property.deedDate})</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> {property.status}</div>
          </div>
        </Card>

        <Card style={{ padding: 18 }}>
          <h4 style={styles.sectionTitle}><BarChart3 size={16} color={C.teal} /> إيرادات شهرية</h4>
          {monthlyBars.length === 0 ? (
            <EmptyState title="لا توجد إيرادات مسجلة" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyBars}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="month" fontSize={10} tick={{ fill: C.textLight }} />
                <YAxis fontSize={10} tick={{ fill: C.textLight }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}` }} formatter={v => [`${fmtNum(v)} ر.س`]} />
                <Bar dataKey="value" fill={C.teal} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Home size={16} color={C.accent} /> الوحدات ({units.length})</h4>
        <DataTable
          columns={[
            { key: "unitNumber",        label: "الوحدة",   nowrap: true },
            { key: "type",              label: "النوع",    nowrap: true },
            { key: "area",              label: "م²",       nowrap: true },
            { key: "monthlyRent",       label: "إيجار/شهر", nowrap: true, render: v => fmtNum(v) },
            { key: "currentTenantName", label: "المستأجر", render: v => v || "—" },
            { key: "status",            label: "الحالة",   nowrap: true },
          ]}
          data={units}
        />
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Users size={16} color="#7c3aed" /> المستأجرون الحاليون ({tenants.length})</h4>
        {tenants.length === 0 ? <EmptyState title="لا يوجد مستأجرون حاليون" /> : (
          <DataTable
            columns={[
              { key: "name",            label: "الاسم" },
              { key: "phone",           label: "الهاتف", nowrap: true },
              { key: "currentUnitName", label: "الوحدة" },
              { key: "status",          label: "الحالة", nowrap: true },
            ]}
            data={tenants}
          />
        )}
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><FileText size={16} color={C.success} /> العقود ({contracts.length})</h4>
        {contracts.length === 0 ? <EmptyState title="لا توجد عقود" /> : (
          <DataTable
            columns={[
              { key: "contractNumber", label: "العقد", nowrap: true },
              { key: "tenantName",     label: "المستأجر" },
              { key: "startDate",      label: "البداية", nowrap: true },
              { key: "endDate",        label: "النهاية", nowrap: true },
              { key: "monthlyRent",    label: "إيجار/شهر", nowrap: true, render: v => fmtNum(v) },
              { key: "status",         label: "الحالة", nowrap: true },
            ]}
            data={contracts}
          />
        )}
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Archive size={16} color={C.textLight} /> المستندات ({docs.length})</h4>
        {docs.length === 0 ? <EmptyState title="لا توجد مستندات" /> : (
          <DataTable
            columns={[
              { key: "type",      label: "النوع", nowrap: true },
              { key: "title",     label: "العنوان" },
              { key: "docNumber", label: "الرقم", nowrap: true },
              { key: "issueDate", label: "الإصدار", nowrap: true },
              { key: "expiryDate", label: "الانتهاء", nowrap: true, render: v => v || "—" },
            ]}
            data={docs}
          />
        )}
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 13) Tenant Profile Tab
   ───────────────────────────────────────────── */
function TenantProfileTab() {
  const { contracts: allContracts, correspondence: allCorrespondence, documents: allDocuments, payments: allPayments, tenants: allTenants } = useWaqfData();
  const [selectedId, setSelectedId] = useState(allTenants[0]?.id || "");
  const tenant = allTenants.find(t => t.id === selectedId);

  if (!tenant) {
    return (
      <div style={{ marginTop: 16 }}>
        <EmptyState title="اختر مستأجراً" subtitle="استخدم القائمة المنسدلة أعلاه لعرض ملف المستأجر" />
      </div>
    );
  }

  const contracts = allContracts.filter(c => c.tenantId === tenant.id);
  const contractIds = contracts.map(c => c.id);
  const payments = allPayments.filter(p => contractIds.includes(p.contractId));
  const correspondence = allCorrespondence.filter(co => co.tenantId === tenant.id);
  const docs = allDocuments.filter(d => d.tenantId === tenant.id);

  const activeContracts = contracts.filter(c => c.status === "ساري" || c.status === "تحت التجديد");
  const pastContracts = contracts.filter(c => c.status === "منتهٍ" || c.status === "ملغى");

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <Field
          label="اختر المستأجر"
          type="select"
          options={allTenants.map(t => ({ value: t.id, label: t.name }))}
          value={selectedId}
          onChange={setSelectedId}
        />
      </Card>

      <Card style={{ padding: 18 }}>
        <h4 style={styles.sectionTitle}><User size={16} color={C.primary} /> البيانات الشخصية</h4>
        <div style={styles.detailGrid}>
          <div style={styles.detailRow}><strong>الاسم:</strong> {tenant.name}</div>
          <div style={styles.detailRow}><strong>الهوية:</strong> {tenant.idNumber}</div>
          <div style={styles.detailRow}><strong>الجنسية:</strong> {tenant.nationality}</div>
          <div style={styles.detailRow}><strong>الهاتف:</strong> {tenant.phone}{tenant.altPhone ? ` / ${tenant.altPhone}` : ""}</div>
          <div style={styles.detailRow}><strong>البريد:</strong> {tenant.email || "—"}</div>
          <div style={styles.detailRow}><strong>المهنة:</strong> {tenant.occupation}</div>
          <div style={styles.detailRow}><strong>جهة العمل:</strong> {tenant.employer}</div>
          <div style={styles.detailRow}><strong>الوحدة الحالية:</strong> {tenant.currentUnitName || "—"}</div>
          <div style={styles.detailRow}><strong>إجمالي العقود:</strong> {tenant.totalContracts}</div>
          <div style={styles.detailRow}><strong>الحالة:</strong> {tenant.status}</div>
        </div>
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><FileText size={16} color={C.success} /> العقود الحالية ({activeContracts.length})</h4>
        {activeContracts.length === 0 ? <EmptyState title="لا توجد عقود حالية" /> : (
          <DataTable
            columns={[
              { key: "contractNumber", label: "العقد", nowrap: true },
              { key: "unitName",       label: "الوحدة" },
              { key: "startDate",      label: "البداية", nowrap: true },
              { key: "endDate",        label: "النهاية", nowrap: true },
              { key: "monthlyRent",    label: "إيجار/شهر", nowrap: true, render: v => fmtNum(v) },
              { key: "status",         label: "الحالة", nowrap: true },
            ]}
            data={activeContracts}
          />
        )}
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Layers size={16} color={C.textLight} /> العقود السابقة ({pastContracts.length})</h4>
        {pastContracts.length === 0 ? <EmptyState title="لا توجد عقود سابقة" /> : (
          <DataTable
            columns={[
              { key: "contractNumber", label: "العقد", nowrap: true },
              { key: "unitName",       label: "الوحدة" },
              { key: "startDate",      label: "البداية", nowrap: true },
              { key: "endDate",        label: "النهاية", nowrap: true },
              { key: "status",         label: "الحالة", nowrap: true },
            ]}
            data={pastContracts}
          />
        )}
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Receipt size={16} color={C.teal} /> سجل الدفعات ({payments.length})</h4>
        {payments.length === 0 ? <EmptyState title="لا توجد دفعات" /> : (
          <DataTable
            columns={[
              { key: "contractNumber", label: "العقد",          nowrap: true },
              { key: "dueDate",        label: "الاستحقاق",      nowrap: true },
              { key: "amount",         label: "المبلغ",         nowrap: true, render: v => fmtNum(v) },
              { key: "paidDate",       label: "تاريخ السداد",   nowrap: true, render: v => v || "—" },
              { key: "status",         label: "الحالة",         nowrap: true, render: v => {
                const s = PAYMENT_STATUSES.find(x => x.key === v);
                return <span style={{ color: paymentStatusColor(v), fontWeight: 600 }}>{s?.label || v}</span>;
              } },
            ]}
            data={payments}
          />
        )}
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><MessageSquare size={16} color={C.accent} /> سجل المراسلات ({correspondence.length})</h4>
        {correspondence.length === 0 ? <EmptyState title="لا توجد مراسلات" /> : (
          <DataTable
            columns={[
              { key: "date",    label: "التاريخ", nowrap: true },
              { key: "type",    label: "النوع",   nowrap: true },
              { key: "subject", label: "الموضوع" },
              { key: "sentVia", label: "أُرسل عبر", nowrap: true },
              { key: "response", label: "الرد", render: v => v || "—" },
            ]}
            data={correspondence}
          />
        )}
      </Card>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Archive size={16} color={C.textLight} /> المستندات ({docs.length})</h4>
        {docs.length === 0 ? <EmptyState title="لا توجد مستندات" /> : (
          <DataTable
            columns={[
              { key: "type",      label: "النوع",  nowrap: true },
              { key: "title",     label: "العنوان" },
              { key: "docNumber", label: "الرقم", nowrap: true },
              { key: "issueDate", label: "الإصدار", nowrap: true },
            ]}
            data={docs}
          />
        )}
      </Card>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 20, marginBottom: 16,
  },
  toolbar: {
    display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer",
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  chartCard: { padding: 20 },
  chartHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16,
  },
  chartTitleWrap: { display: "flex", alignItems: "center", gap: 8 },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: {
    display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8,
  },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },
  legendDot: { width: 10, height: 10, borderRadius: 3 },
  legendLabel: { fontSize: 11, color: C.textLight },
  legendValue: { fontSize: 11, fontWeight: 600, color: C.primary },
  sectionTitle: {
    display: "flex", alignItems: "center", gap: 8,
    margin: 0, marginBottom: 14, fontSize: 14, fontWeight: 700, color: C.primary,
  },
  quickLinks: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10,
  },
  quickBtn: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 14px", background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 10, cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
    fontSize: 13, fontWeight: 600, color: C.primary,
  },
  detailGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
  },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.7 },
  alertHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4,
  },
  alertCount: {
    padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
  },
  profileGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 16,
  },
};
